import { ChevronRight } from 'lucide-react'
import { Link } from 'react-router-dom'

import type { DmPolicy } from '@/api/types'
import { PageHeader } from '@/app/layouts/PageHeader'
import { CenteredSpinner } from '@/components/ui/Spinner'
import { useCurrentUser } from '@/features/auth'
import { useUpdateProfile } from '@/features/profile'
import { SettingsSection, Toggle, usePreferences, useUpdatePreferences } from '@/features/settings'
import { useT } from '@/i18n'
import type { MessageKey } from '@/i18n'
import { cn } from '@/lib/cn'

const DM_OPTIONS: { value: DmPolicy; label: MessageKey; hint: MessageKey }[] = [
  { value: 'following', label: 'privacy.dmFollowing', hint: 'privacy.dmFollowingHint' },
  { value: 'everyone', label: 'privacy.dmEveryone', hint: 'privacy.dmEveryoneHint' },
]

/** 设置 → 隐私：保护我的帖子（D75）、关注请求（D76）、谁可以给我发私信（D70）。 */
export function SettingsPrivacyPage() {
  const t = useT()
  const { user } = useCurrentUser()
  const { data: prefs, isPending, isError } = usePreferences()
  const update = useUpdatePreferences()
  const updateProfile = useUpdateProfile()
  const pending = user?.follow_requests_count ?? 0

  return (
    <>
      <PageHeader title={t('privacy.title')} back />
      {user && (
        <SettingsSection title={t('privacy.protectTitle')}>
          <Toggle
            label={t('privacy.protectLabel')}
            description={t('privacy.protectDesc')}
            checked={user.is_private}
            onChange={(checked) => updateProfile.mutate({ is_private: checked })}
          />
          {(user.is_private || pending > 0) && (
            <Link
              to="/follow-requests"
              className="mt-2 flex items-center justify-between py-2 text-[15px] font-bold hover:underline"
            >
              <span>{t('followRequests.title')}</span>
              <span className="flex items-center gap-1 text-ink-muted">
                {pending > 0 && <span className="font-mono">{pending}</span>}
                <ChevronRight className="size-4" aria-hidden />
              </span>
            </Link>
          )}
        </SettingsSection>
      )}
      {isPending && <CenteredSpinner />}
      {isError && (
        <p className="px-4 py-6 text-[15px] text-ink-muted">{t('notifPref.loadFailed')}</p>
      )}
      {prefs && (
        <SettingsSection title={t('privacy.dmTitle')} description={t('privacy.dmDesc')}>
          <div role="radiogroup" aria-label={t('privacy.dmTitle')} className="divide-y divide-line">
            {DM_OPTIONS.map((option) => {
              const active = prefs.dm_policy === option.value
              return (
                <button
                  key={option.value}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  disabled={update.isPending}
                  onClick={() => !active && update.mutate({ dm_policy: option.value })}
                  className="flex w-full items-start gap-3 py-3 text-left"
                >
                  <span
                    aria-hidden
                    className={cn(
                      'mt-1 size-4 shrink-0 rounded-full border-2',
                      active ? 'border-brand-ink bg-brand-ink' : 'border-line-strong',
                    )}
                  />
                  <span>
                    <span className="block text-[15px] font-bold">{t(option.label)}</span>
                    <span className="block text-[13px] text-ink-muted">{t(option.hint)}</span>
                  </span>
                </button>
              )
            })}
          </div>
        </SettingsSection>
      )}
    </>
  )
}
